// Zugriff auf die Umzugsscan-API. Der Server antwortet bei Fehlern immer mit
// { code, ...details } (server/src/fehler.ts); hier wird daraus ApiError.
// Keine Verbindung → NetworkError (die Warteschlange versucht es später erneut).

import type { PhotoStep } from "@umzugsscan/shared";

export class ApiError extends Error {
  status: number;
  code: string;
  details: Record<string, unknown>;
  constructor(status: number, code: string, details: Record<string, unknown>) {
    super(`${status} ${code}`);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export class NetworkError extends Error {
  constructor(cause: unknown) {
    super("Keine Verbindung");
    this.name = "NetworkError";
    this.cause = cause;
  }
}

export type PhotoUpload = {
  photoId: string;
  roomId: string;
  step: PhotoStep;
  section?: number | null;
  blurScore: number | null;
  brightness: number | null;
};

export type Api = ReturnType<typeof createApi>;

export function createApi(base: string, fetchImpl: typeof fetch = (...a) => fetch(...a)) {
  async function request<T = unknown>(
    method: string,
    path: string,
    body?: unknown,
    contentType = "application/json",
  ): Promise<T> {
    let res: Response;
    try {
      res = await fetchImpl(base + path, {
        method,
        headers: body === undefined ? {} : { "content-type": contentType },
        body: body === undefined ? undefined : body instanceof Blob ? body : JSON.stringify(body),
      });
    } catch (e) {
      throw new NetworkError(e);
    }
    if (res.status === 204) return undefined as T;
    let data: unknown = null;
    try {
      data = await res.json();
    } catch {
      // kein JSON (z. B. Proxy-Fehlerseite)
    }
    if (!res.ok) {
      const { code, ...details } = (data ?? {}) as { code?: string };
      throw new ApiError(res.status, code ?? "serverfehler", details);
    }
    return data as T;
  }

  const survey = (token: string) => `/erfassung/${encodeURIComponent(token)}`;

  return {
    request,
    survey,
    start: <T>(input: { slug: string } | { invite: string }) => request<T>("POST", "/erfassung/start", input),
    load: <T>(token: string) => request<T>("GET", survey(token)),
    submit: <T>(token: string) => request<T>("POST", `${survey(token)}/absenden`),
    precheck: <T>(token: string) => request<T>("POST", `${survey(token)}/vorpruefung`),
    hall: <T>(token: string, roomId: string) => request<T>("POST", `${survey(token)}/flur/${roomId}`),
    roomDoors: <T>(token: string, roomId: string) => request<T>("POST", `${survey(token)}/raum/${roomId}/tueren`),
    putPhoto(token: string, p: PhotoUpload, blob: Blob) {
      const q = new URLSearchParams({ roomId: p.roomId, step: p.step });
      if (p.blurScore !== null) q.set("blurScore", String(p.blurScore));
      if (p.brightness !== null) q.set("brightness", String(p.brightness));
      if (p.section != null) q.set("section", String(p.section));
      return request("PUT", `${survey(token)}/fotos/${p.photoId}?${q}`, blob, "image/jpeg");
    },
  };
}

// import.meta.env gibt es nur unter Vite (in den Node-Tests nicht).
export const API_URL: string = import.meta.env?.VITE_API_URL ?? "http://localhost:3200";
/** Gemeinsame Instanz für die App (Tests bauen sich eigene mit createApi). */
export const api = createApi(API_URL);
